import React from 'react';
import { Message, Product } from '@/types';
import { ProductCard } from './ProductCard';
import { User, Bot, Clock } from 'lucide-react';
import { clsx } from 'clsx';

interface ChatMessageProps {
  message: Message;
  onProductClick?: (product: Product) => void;
  showProducts?: boolean;
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, onProductClick, showProducts = true }) => {
  const formatTime = (date: Date) => {
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div
      className={clsx(
        'flex mb-4 animate-fadeIn',
        message.isUser ? 'justify-end' : 'justify-start'
      )}
    >
      <div
        className={clsx(
          'max-w-[80%] flex',
          message.isUser ? 'flex-row-reverse' : 'flex-row'
        )}
      >
        {/* Avatar */}
        <div
          className={clsx(
            'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
            message.isUser
              ? 'bg-blue-500 text-white ml-3'
              : 'bg-purple-500 text-white mr-3'
          )}
        >
          {message.isUser ? <User size={16} /> : <Bot size={16} />}
        </div>

        {/* Message Content */}
        <div
          className={clsx(
            'rounded-2xl px-4 py-3 shadow-sm',
            message.isUser
              ? 'bg-blue-500 text-white rounded-br-sm'
              : 'bg-gray-100 text-gray-800 rounded-bl-sm'
          )}
        >
          {/* Text Content */}
          {message.content && (
            <div className="text-sm leading-relaxed whitespace-pre-wrap">
              {message.content}
            </div>
          )}

          {/* Image Content */}
          {message.image && (
            <div className="mt-2">
              <img
                src={message.image}
                alt="Uploaded image"
                className="max-w-full h-auto rounded-lg border border-gray-200"
                style={{ maxHeight: '200px', maxWidth: '200px' }}
              />
            </div>
          )}

          {/* Products - Only show if enabled */}
          {message.products && message.products.length > 0 && showProducts && (
            <div className="mt-3 space-y-3">
              {message.products.map((product, index) => (
                <ProductCard
                  key={`${message.id}-product-${index}`}
                  product={product}
                  onClick={onProductClick}
                  variant="compact"
                />
              ))}
            </div>
          )}

          {/* Timestamp */}
          <div
            className={clsx(
              'flex items-center mt-2 text-xs opacity-70',
              message.isUser ? 'justify-end' : 'justify-start'
            )}
          >
            <Clock size={12} className="mr-1" />
            {formatTime(message.timestamp)}
          </div>
        </div>
      </div>
    </div>
  );
};

// Typing indicator component
export const TypingIndicator: React.FC = () => {
  return (
    <div className="flex justify-start mb-4">
      <div className="flex flex-row">
        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-500 text-white mr-3 flex items-center justify-center">
          <Bot size={16} />
        </div>
        <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
            <div
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0.1s' }}
            ></div>
            <div
              className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
              style={{ animationDelay: '0.2s' }}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
};